import type { Pinia } from 'pinia'
import type { Router } from 'vue-router'

import { useAuthStore } from '@/features/auth/model'
import { setPasswordChangeRequiredHandler, setSessionExpiredHandler } from '@/shared/api/http'

/**
 * What the application does when the server ends or narrows the session (SPEC.md §8.1).
 *
 * Registered here rather than inside the HTTP module so transport code stays free of application
 * state (§10.1): the client only reports, the app decides where the user goes.
 *
 * - **The session is gone** (a 401 the refresh cookie cannot cure): drop the local session and go
 *   to the login screen.
 * - **A temporary password must be replaced first** (`403 PASSWORD_CHANGE_REQUIRED`): re-read the
 *   profile and go to the change-password screen. No toast — the screen is the explanation — and
 *   the many requests a page fires at once lead to one navigation, not one each.
 *
 * @example
 * installSessionHandlers(router, pinia) // once, in main.ts
 */
export function installSessionHandlers(router: Router, pinia: Pinia): void {
  setSessionExpiredHandler(() => {
    const auth = useAuthStore(pinia)
    auth.clear()
    if (router.currentRoute.value.name !== 'login') {
      void router.push({ name: 'login' })
    }
  })

  let inFlight: Promise<void> | null = null
  setPasswordChangeRequiredHandler(() => {
    if (inFlight) return
    inFlight = (async () => {
      await useAuthStore(pinia).requirePasswordChange()
      if (router.currentRoute.value.name !== 'change-password') {
        await router.push({ name: 'change-password', query: { reason: 'required' } })
      }
    })().finally(() => {
      inFlight = null
    })
  })
}
